import React, { useContext, useState } from 'react';
import { ContextProps } from '../types/ContextProps';
import { ValidatedNoonReport } from '../types/ViewModel/ValidatedNoonReport';
import { AolDataItem, AolGroup, AolItem } from '../types/ViewModel/AolItem';
import { SaveAolEvent } from '../app/reducers/AolSaveEventReducer';
import { SyncAolEvent } from '../app/reducers/AolPortLogSyncReducer';
import { AolFilter } from '../types/AolFilter';
import { VoyageFuelPrice } from '../types/ViewModel/VoyageFuelPrice';
import { useAuthStore } from './AuthContext';
import { RoleTypeEnum } from '../enums/roleTypeEnum';
import { Period } from '../enums/PeriodEnum';
import { getDateNow, subtractDateDays } from 'odfjell-date-time-utils/dateTimeUtils';
import { AolPageEnum } from 'src/enums/aol/AolPageEnum';
import { TankersEvent } from '../types/ViewModel/TankersEvent';

export type DeletedAolSnapshot = {
    noonReportGuid: string;
    imoNumber: number;
    noonReport: ValidatedNoonReport;
    timestamp: number;
};

type AolContextType = {
    deletedAolSnapshots: Map<string, DeletedAolSnapshot>;
    setDeletedAolSnapshots: React.Dispatch<React.SetStateAction<Map<string, DeletedAolSnapshot>>>;
    filterTimelineAfterDate: Date | null;
    selectedTimelineItem: AolDataItem | null;
    selectedAolDataItem: AolDataItem | null;
    selectedAolGroup: AolGroup | null;
    aolDataItems: AolDataItem[];
    aolGroups: AolGroup[];
    aolEventsArray: TankersEvent[] | null;
    aolEventsVesselImo: number | null;
    saveAolEvent: SaveAolEvent | null;
    aolSyncEvent: SyncAolEvent | null;
    isUpdatingAolGroupStatus: boolean;
    previousAolGroup: AolGroup | null;
    selectedUnit: string;
    isSummaryVisible: boolean;
    isCommentsVisible: boolean;
    isEditGloballyMode: boolean;
    lastAolFetchTime: Date | null;
    aolGroupStatusMessage: string | null;
    aolFilter: AolFilter;
    defaultAolFilter: AolFilter;
    isSaveConfig: boolean | undefined;
    aolItems: AolItem[] | null;
    noonReports: ValidatedNoonReport[] | null;
    voyageFuelPrices: VoyageFuelPrice[];
    navtorDistance: number | null;
    period: Period;
    aolListSortAsc: boolean;
    hasSyncError: boolean;
    activeAolPage: AolPageEnum | null;
    setFilterTimelineAfterDate: (timelineFilterDate: Date | null) => void;
    setSelectedTimelineItem: (date: AolDataItem | null) => void;
    setSaveAolEvent: (event: SaveAolEvent | null) => void;
    setAolSyncEvent: (event: SyncAolEvent | null) => void;
    setSelectedAolDataItem: (selectedAolDataItem: AolDataItem | null) => void;
    setSelectedAolGroup: (selectedAolGroup: AolGroup | null) => void;
    setAolDataItems: (aolDataItems: AolDataItem[]) => void;
    setAolGroups: (aolGroups: AolGroup[]) => void;
    setIsUpdatingAolGroupStatus: (isUpdating: boolean) => void;
    setPreviousAolGroup: (aolGroup: AolGroup | null) => void;
    setSelectedUnit: (selectedUnit: string) => void;
    setIsSummaryVisible: (isVisible: boolean) => void;
    setIsCommentsVisible: (isVisible: boolean) => void;
    setIsEditGloballyMode: (isEditGloballyMode: boolean) => void;
    setLastAolFetchTime: (lastAolFetchTime: Date) => void;
    setAolGroupStatusMessage: (message: string | null) => void;
    setAolFilter: (aolFilter: AolFilter) => void;
    setIsSaveConfig: (isSave: boolean | undefined) => void;
    setAolItems: (aolItems: AolItem[] | null) => void;
    setNoonReports: (noonReports: ValidatedNoonReport[] | null) => void;
    setVoyageFuelPrices: (voyageFuelPrices: VoyageFuelPrice[]) => void;
    setNavtorDistance: (distance: number | null) => void;
    setPeriod: (period: Period) => void;
    setAolListSortAsc: (sortAsc: boolean) => void;
    setDefaultAolFilter: (aolFilter: AolFilter) => void;
    setHasSyncError: (hasError: boolean) => void;
    setActiveAolPage: (page: AolPageEnum | null) => void;
    aolEventsLoadedRange: { from: string; to: string } | null;
    setAolEventsArray: (events: TankersEvent[] | null) => void;
    setAolEventsVesselImo: (imoNumber: number | null) => void;
    setAolEventsLoadedRange: (range: { from: string; to: string } | null) => void;
};

export const AolContext = React.createContext<AolContextType>(undefined!);

export const AolProvider = ({ children }: ContextProps) => {
    const { role } = useAuthStore();
    const aolToDate = getDateNow();
    const aolFromDate = subtractDateDays(getDateNow(), role?.roleType === RoleTypeEnum.ShipUser ? 14 : 28);
    const [defaultAolFilter, setDefaultAolFilter] = useState<AolFilter>({
        fromDate: aolFromDate,
        toDate: aolToDate,
        isDefault: true,
    });

    const [deletedAolSnapshots, setDeletedAolSnapshots] = useState<Map<string, DeletedAolSnapshot>>(new Map());
    const [filterTimelineAfterDate, setFilterTimelineAfterDate] = useState<Date | null>(null);
    const [selectedTimelineItem, setSelectedTimelineItem] = useState<AolDataItem | null>(null);
    const [selectedAolDataItem, setSelectedAolDataItem] = useState<AolDataItem | null>(null);
    const [selectedAolGroup, setSelectedAolGroup] = useState<AolGroup | null>(null);
    const [aolDataItems, setAolDataItems] = useState<AolDataItem[]>([]);
    const [aolGroups, setAolGroups] = useState<AolGroup[]>([]);
    const [saveAolEvent, setSaveAolEvent] = useState<SaveAolEvent | null>(null);
    const [aolSyncEvent, setAolSyncEvent] = useState<SyncAolEvent | null>(null);
    const [isUpdatingAolGroupStatus, setIsUpdatingAolGroupStatus] = useState<boolean>(false);
    const [previousAolGroup, setPreviousAolGroup] = useState<AolGroup | null>(null);
    const [aolGroupStatusMessage, setAolGroupStatusMessage] = useState<string | null>(null);
    const [selectedUnit, setSelectedUnit] = useState<string>('MT');
    const [isSummaryVisible, setIsSummaryVisible] = useState(false);
    const [isCommentsVisible, setIsCommentsVisible] = useState(true);
    const [isEditGloballyMode, setIsEditGloballyMode] = useState(false);
    const [lastAolFetchTime, setLastAolFetchTime] = useState<Date | null>(null);
    const [aolFilter, setAolFilter] = useState<AolFilter | null>(null);
    const [isSaveConfig, setIsSaveConfig] = useState<boolean | undefined>(undefined);
    const [aolItems, setAolItems] = useState<AolItem[] | null>(null);
    const [noonReports, setNoonReports] = useState<ValidatedNoonReport[] | null>(null);
    const [voyageFuelPrices, setVoyageFuelPrices] = useState<VoyageFuelPrice[]>([]);
    const [navtorDistance, setNavtorDistance] = useState<number>(null);
    const [period, setPeriod] = useState<Period>(Period.TwoWeeks);
    const [aolListSortAsc, setAolListSortAsc] = useState<boolean>(true);
    const [hasSyncError, setHasSyncError] = useState<boolean>(false);
    const [activeAolPage, setActiveAolPage] = useState<AolPageEnum | null>(null);
    const [aolEventsArray, setAolEventsArray] = useState<TankersEvent[] | null>(null);
    const [aolEventsVesselImo, setAolEventsVesselImo] = useState<number | null>(null);
    const [aolEventsLoadedRange, setAolEventsLoadedRange] = useState<{ from: string; to: string } | null>(null);

    return (
        <AolContext.Provider
            value={{
                filterTimelineAfterDate,
                setFilterTimelineAfterDate,
                selectedTimelineItem,
                setSelectedTimelineItem,
                selectedAolDataItem,
                setSelectedAolDataItem,
                selectedAolGroup,
                setSelectedAolGroup,
                aolDataItems,
                setAolDataItems,
                saveAolEvent,
                setSaveAolEvent,
                aolSyncEvent,
                setAolSyncEvent,
                aolGroups,
                setAolGroups,
                selectedUnit,
                setSelectedUnit,
                deletedAolSnapshots,
                setDeletedAolSnapshots,
                isUpdatingAolGroupStatus,
                setIsUpdatingAolGroupStatus,
                previousAolGroup,
                setPreviousAolGroup,
                isSummaryVisible,
                setIsSummaryVisible,
                isCommentsVisible,
                setIsCommentsVisible,
                isEditGloballyMode,
                setIsEditGloballyMode,
                aolGroupStatusMessage,
                setAolGroupStatusMessage,
                lastAolFetchTime,
                setLastAolFetchTime,
                aolFilter,
                setAolFilter,
                defaultAolFilter,
                isSaveConfig,
                setIsSaveConfig,
                aolItems,
                setAolItems,
                noonReports,
                setNoonReports,
                voyageFuelPrices,
                setVoyageFuelPrices,
                navtorDistance,
                setNavtorDistance,
                period,
                setPeriod,
                aolListSortAsc,
                setAolListSortAsc,
                setDefaultAolFilter,
                hasSyncError,
                setHasSyncError,
                activeAolPage,
                setActiveAolPage,
                aolEventsArray,
                setAolEventsArray,
                aolEventsVesselImo,
                setAolEventsVesselImo,
                aolEventsLoadedRange,
                setAolEventsLoadedRange,
            }}
        >
            {children}
        </AolContext.Provider>
    );
};

export const useAolStore = () => useContext(AolContext);
